/*
 * WIND content forms (ADR 0073, docs/architecture/wind-contract.md section 3): the raid rules' Wind fieldsets (`raidRules.wind`),
 * the Wind tiers page (`winds[]`, one row per tier, order = tier index) and the shared inputs the other forms use for wind fields
 * (a tier select of the draft's `winds[]` ids, number lists). Absent fields show the C# defaults from `lib/wind/defaults.ts`;
 * nothing is written to the draft until a field is edited. Clamps are the contract's, as min/max on the inputs; the rules
 * themselves are the validators' (lib/validate).
 */
import { useId } from 'react';
import type { Issue } from '../../../lib/validate';
import {
  windAsParsed,
  windTierIds,
  WIND_TIER_DEFAULTS,
  type WindDef,
  type WindParsed,
  type WindTierDef,
} from '../../../lib/wind/defaults';
import {
  CheckboxField,
  Field,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectInput,
  TextField,
} from '../fields';
import { useContentRefs } from '../refs';
import type { FormProps } from '../types';

/** The tier ids a tier select offers: the draft's `winds[]`, or the default FRESH / WINDED / GASSED while it has none. */
function useWindTierOptions() {
  const { winds } = useContentRefs();
  const byId = new Map((winds ?? []).map((w) => [w.id ?? '', w.name ?? '']));
  const fromDraft = (winds ?? []).length > 0;
  return windTierIds(winds).map((id, i) => ({
    value: id,
    label: `${i}: ${(fromDraft ? byId.get(id) : WIND_TIER_DEFAULTS[i]?.name) || id} (${id})`,
  }));
}

/** A select of the draft's wind tier ids (an id that isn't listed stays selectable, marked). */
export function TierSelectField({
  label,
  hint,
  value,
  onChange,
  issues,
}: {
  label: string;
  hint?: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  issues?: Issue[];
}) {
  const options = useWindTierOptions();
  return (
    <Field label={label} hint={hint} issues={issues}>
      {(id, invalid) => (
        <SelectInput
          id={id}
          invalid={invalid}
          value={value}
          options={options}
          onChange={(v) => onChange(v ?? '')}
        />
      )}
    </Field>
  );
}

/** A list of numbers with add/remove/reorder (e.g. `wind.wounds.capacityMultByWounds`). */
export function NumberListField({
  label,
  hint,
  value,
  onChange,
  issues,
  rowIssues,
  min,
  max,
  step,
  make,
}: {
  label: string;
  hint?: string;
  value: number[];
  onChange: (next: number[]) => void;
  issues?: Issue[];
  rowIssues?: (i: number) => Issue[] | undefined;
  min?: number;
  max?: number;
  step?: number;
  make: () => number;
}) {
  return (
    <ListEditor
      label={label}
      hint={hint}
      items={value}
      onChange={onChange}
      what="number"
      issues={issues}
      make={make}
      row={(n, i, set) => (
        <>
          <NumberInput
            label={`${label} ${i + 1}`}
            value={n}
            min={min}
            max={max}
            step={step}
            onChange={(v) => set(v ?? 0)}
          />
          <IssueList issues={rowIssues?.(i)} />
        </>
      )}
    />
  );
}

/** A fixed-length row of numbers with a label each (e.g. `healthRules.levelWind.recovery`, five activity levels). */
export function FixedNumbersField({
  label,
  hint,
  names,
  value,
  onChange,
  issues,
  min,
  max,
  step,
}: {
  label: string;
  hint?: string;
  names: readonly string[];
  value: number[];
  onChange: (next: number[]) => void;
  issues?: Issue[];
  min?: number;
  max?: number;
  step?: number;
}) {
  const id = useId();
  return (
    <div className="sm:col-span-2" role="group" aria-labelledby={id}>
      <span id={id} className="block text-xs font-semibold uppercase tracking-wide">
        {label}
      </span>
      <div className="grid grid-cols-5 gap-2">
        {names.map((n, i) => (
          <label key={n} className="text-xs">
            {n}
            <NumberInput
              label={`${label} ${n}`}
              value={value[i]}
              min={min}
              max={max}
              step={step}
              onChange={(v) => {
                const next = names.map((_, j) => value[j] ?? 0);
                next[i] = v ?? 0;
                onChange(next);
              }}
            />
          </label>
        ))}
      </div>
      {hint && <p className="mt-0.5 text-xs text-bruise">{hint}</p>}
      <IssueList issues={issues} />
    </div>
  );
}

type Wind = WindParsed;
type RaidWithWind = { wind?: WindDef | null };

/**
 * The raid rules' Wind fieldsets (`raidRules.wind`, contract 3.1). `value` is the whole raid rules block; edits write the
 * whole wind block back, defaults included, the way the server reads it.
 */
export function RaidWindFieldsets<T extends RaidWithWind>({
  value: r,
  onChange,
  errors,
}: FormProps<T>) {
  const w: Wind = windAsParsed(r.wind);
  const put = (next: Wind) => onChange({ ...r, wind: next });
  const sub =
    <K extends keyof Wind>(block: K) =>
    (k: keyof Wind[K] & string, v: unknown) =>
      put({ ...w, [block]: { ...(w[block] as object), [k]: v } });
  const setDrop = sub('drop');
  const setRecovery = sub('recovery');
  const setSprint = sub('sprint');
  const setCosts = sub('costs');
  const setBite = sub('bite');
  const setHits = sub('hits');
  const setWounds = sub('wounds');
  const setRest = sub('rest');
  const e = (path: string) => errors[`wind.${path}`];
  const num = (
    label: string,
    path: string,
    v: number | undefined,
    set: (v: number) => void,
    o: { min: number; max: number; step?: number; integer?: boolean; hint?: string },
  ) => (
    <NumberField
      key={path}
      label={label}
      hint={o.hint ?? `${o.min}..${o.max}.`}
      min={o.min}
      max={o.max}
      step={o.step ?? (o.integer ? 1 : 0.05)}
      integer={o.integer}
      value={v}
      issues={e(path)}
      onChange={(n) => set(n ?? 0)}
    />
  );
  const { recovery: rc, costs, bite, wounds, rest } = w;
  const hours = rest.hoursPerWind;
  const setHours = (k: keyof typeof hours & string, v: number) =>
    setRest('hoursPerWind', { ...hours, [k]: v });

  return (
    <>
      <Fieldset legend="Wind: the tank (WIND, ADR 0073)">
        <IssueList issues={errors.wind} />
        {num('Capacity', 'capacity', w.capacity, (v) => put({ ...w, capacity: v }), {
          min: 40,
          max: 300,
          integer: true,
          hint: '40..300. Wind points in a FRESH tank (x the tier, wounds and the sheet).',
        })}
        {num('Drop grace (s)', 'drop.graceS', w.drop.graceS, (v) => setDrop('graceS', v), {
          min: 0,
          max: 3,
          hint: '0..3. An empty tank waits this long before it drops a tier.',
        })}
        {num('Drop lands at (share)', 'drop.landFrac', w.drop.landFrac, (v) => setDrop('landFrac', v), {
          min: 0,
          max: 1,
          hint: '0..1. The bar after a drop, as a share of the smaller tank (Second Wind still lands full).',
        })}
        {num(
          'Sprint cost per second',
          'sprint.costPerS',
          w.sprint.costPerS,
          (v) => setSprint('costPerS', v),
          { min: 0.1, max: 30 },
        )}
        {num(
          'Sprint needs at least',
          'sprint.minToStart',
          w.sprint.minToStart,
          (v) => setSprint('minToStart', v),
          { min: 0, max: w.capacity, step: 1, hint: '0..capacity. Wind needed to start a sprint.' },
        )}
        {num(
          'Hit damage to wind',
          'hits.damageToWind',
          w.hits.damageToWind,
          (v) => setHits('damageToWind', v),
          { min: 0, max: 5, hint: "0..5. A hit's damage becomes this many wind points." },
        )}
      </Fieldset>
      <Fieldset legend="Wind: recovery">
        {num(
          'Pause after spending (s)',
          'recovery.pauseS',
          rc.pauseS,
          (v) => setRecovery('pauseS', v),
          {
            min: 0,
            max: 5,
          },
        )}
        {num(
          'Pause after a drop (s)',
          'recovery.pauseAfterDropS',
          rc.pauseAfterDropS,
          (v) => setRecovery('pauseAfterDropS', v),
          { min: 0, max: 10 },
        )}
        {num(
          'Fast recovery per second',
          'recovery.fastPerS',
          rc.fastPerS,
          (v) => setRecovery('fastPerS', v),
          {
            min: 0.1,
            max: 50,
          },
        )}
        {num(
          'Fast until (share of the tank)',
          'recovery.fastUntilFrac',
          rc.fastUntilFrac,
          (v) => setRecovery('fastUntilFrac', v),
          { min: 0, max: 1 },
        )}
        {num(
          'Slow recovery per second',
          'recovery.slowPerS',
          rc.slowPerS,
          (v) => setRecovery('slowPerS', v),
          {
            min: 0.1,
            max: Math.min(50, rc.fastPerS ?? 50),
            hint: '0.1..50, at most the fast rate.',
          },
        )}
        {num(
          'While walking (multiplier)',
          'recovery.walkMult',
          rc.walkMult,
          (v) => setRecovery('walkMult', v),
          {
            min: 0,
            max: 1,
          },
        )}
        {num(
          'While crouching (multiplier)',
          'recovery.crouchMult',
          rc.crouchMult,
          (v) => setRecovery('crouchMult', v),
          { min: 0, max: 1 },
        )}
        {num(
          'Holding under threat (multiplier)',
          'recovery.holdUnderThreatMult',
          rc.holdUnderThreatMult,
          (v) => setRecovery('holdUnderThreatMult', v),
          { min: 0, max: 1 },
        )}
        <CheckboxField
          label="Threat gate"
          hint="A sim switch: off only in tests."
          checked={rc.threatGate ?? true}
          onChange={(v) => setRecovery('threatGate', v)}
        />
      </Fieldset>
      <Fieldset legend="Wind: costs">
        {num('Push-off', 'costs.pushOff', costs.pushOff, (v) => setCosts('pushOff', v), {
          min: 0,
          max: 50,
          step: 1,
        })}
        {num('Takedown', 'costs.takedown', costs.takedown, (v) => setCosts('takedown', v), {
          min: 0,
          max: 50,
          step: 1,
        })}
        {num('Kick', 'costs.kick', costs.kick, (v) => setCosts('kick', v), {
          min: 0,
          max: 50,
          step: 1,
        })}
        {num('Throw', 'costs.throw', costs.throw, (v) => setCosts('throw', v), {
          min: 0,
          max: 50,
          step: 1,
        })}
        {num('Reload', 'costs.reload', costs.reload, (v) => setCosts('reload', v), {
          min: 0,
          max: 20,
          step: 0.5,
          hint: '0..20, once per reload.',
        })}
        {num(
          'Sustained fire window (s)',
          'costs.sustainedFireWindowS',
          costs.sustainedFireWindowS,
          (v) => setCosts('sustainedFireWindowS', v),
          {
            min: 0.05,
            max: 2,
            hint: "0.05..2. A weapon's wind cost per second is charged while a shot was fired this recently.",
          },
        )}
      </Fieldset>
      <Fieldset legend="Wind: bites and wounds">
        {num('Base bite odds', 'bite.baseOdds', bite.baseOdds, (v) => setBite('baseOdds', v), {
          min: 0,
          max: 1,
          step: 0.01,
          hint: "0..1. A push-off's bite roll before the tier and armor.",
        })}
        {num('Spent-tank factor', 'bite.spentK', bite.spentK, (v) => setBite('spentK', v), {
          min: 0,
          max: 10,
          hint: '0..10. Odds x (1 + this x the spent share of the tank).',
        })}
        {num('Max bite odds', 'bite.maxOdds', bite.maxOdds, (v) => setBite('maxOdds', v), {
          min: 0,
          max: 1,
        })}
        {num('Wounds to die (player)', 'wounds.toDie', wounds.toDie, (v) => setWounds('toDie', v), {
          min: 1,
          max: 10,
          integer: true,
          hint: '1..10.',
        })}
        {num(
          'Wounds to down (teammates)',
          'wounds.toDown',
          wounds.toDown,
          (v) => setWounds('toDown', v),
          {
            min: 1,
            max: wounds.toDie ?? 10,
            integer: true,
            hint: '1..wounds to die. Dead at the next wound.',
          },
        )}
        <NumberListField
          label="Tank multiplier by wounds"
          hint="Index = wound capacity steps taken; the last repeats. Each 0.1..1, non-increasing, at least one."
          value={wounds.capacityMultByWounds ?? []}
          onChange={(v) => setWounds('capacityMultByWounds', v)}
          issues={e('wounds.capacityMultByWounds')}
          rowIssues={(i) => e(`wounds.capacityMultByWounds.${i}`)}
          min={0.1}
          max={1}
          step={0.05}
          make={() => {
            const l = wounds.capacityMultByWounds ?? [];
            return l[l.length - 1] ?? 1;
          }}
        />
      </Fieldset>
      <Fieldset legend="Wind: rest debt between raids">
        {num(
          'Hours per wind at Home',
          'rest.hoursPerWind.home',
          hours.home,
          (v) => setHours('home', v),
          {
            min: 0.1,
            max: 48,
            step: 0.5,
          },
        )}
        {num(
          'Hours per wind at an outpost',
          'rest.hoursPerWind.outpost',
          hours.outpost,
          (v) => setHours('outpost', v),
          { min: 0.1, max: 48, step: 0.5 },
        )}
        {num(
          'Hours per wind away',
          'rest.hoursPerWind.away',
          hours.away,
          (v) => setHours('away', v),
          {
            min: 0.1,
            max: 48,
            step: 0.5,
          },
        )}
        {num(
          'Debt per wound',
          'rest.debtPerWound',
          rest.debtPerWound,
          (v) => setRest('debtPerWound', v),
          {
            min: 0,
            max: 3,
          },
        )}
        {num('Max debt (winds)', 'rest.maxDebt', rest.maxDebt, (v) => setRest('maxDebt', v), {
          min: 0,
          max: 10,
          step: 0.5,
        })}
        <TierSelectField
          label="Start floor tier"
          hint="Debt never starts a raid deeper than this tier."
          value={rest.startFloorTier}
          issues={e('rest.startFloorTier')}
          onChange={(v) => setRest('startFloorTier', v)}
        />
        <TierSelectField
          label="v4 resume tier"
          hint="A wind build resuming an older (v4) raid ticket starts at this tier."
          value={w.startTier.v4ResumeTier}
          issues={e('startTier.v4ResumeTier')}
          onChange={(v) => put({ ...w, startTier: { ...w.startTier, v4ResumeTier: v } })}
        />
        <WindTiersSummary />
      </Fieldset>
    </>
  );
}

/** The draft's tiers at a glance (edited on the Wind tiers page). */
function WindTiersSummary() {
  const { winds } = useContentRefs();
  const rows = (winds ?? []).length > 0 ? winds : null;
  return (
    <div className="sm:col-span-2 text-xs">
      <p className="font-semibold uppercase tracking-wide">Wind tiers</p>
      {rows ? (
        <p className="text-bruise">
          {rows.map((t, i) => `${i}: ${t.name || t.id} ${t.capacityMult ?? 1}x`).join(' · ')} (edit
          them on the Wind tiers page).
        </p>
      ) : (
        <p className="text-bruise">
          The draft has no wind tiers yet: the selects offer the default FRESH, WINDED and GASSED.
          Add them on the Wind tiers page.
        </p>
      )}
    </div>
  );
}

/** One `winds[]` row (contract 3.2). Order on the page is the tier index; absent fields are the C# defaults. */
export function WindTierForm({ value, onChange, errors }: FormProps<WindTierDef>) {
  const t: Required<WindTierDef> = {
    ...(WIND_TIER_DEFAULTS[0] as Required<WindTierDef>),
    id: '',
    name: '',
    colorSlot: '',
    ...value,
  };
  const set = <K extends keyof WindTierDef>(k: K, v: WindTierDef[K]) => onChange({ ...t, [k]: v });
  const num = (
    k: keyof WindTierDef & string,
    label: string,
    min: number,
    max: number,
    hint: string,
    step = 0.05,
  ) => (
    <NumberField
      key={k}
      label={label}
      hint={`${min}..${max}. ${hint}`}
      min={min}
      max={max}
      step={step}
      value={t[k] as number}
      issues={errors[k]}
      onChange={(v) => set(k, v ?? 0)}
    />
  );
  return (
    <>
      <Fieldset legend="Tier">
        <TextField label="Id" mono value={t.id} issues={errors.id} onChange={(v) => set('id', v)} />
        <TextField
          label="Name"
          hint="The print label (FRESH, WINDED, GASSED)."
          value={t.name}
          issues={errors.name}
          onChange={(v) => set('name', v)}
        />
        <TextField
          label="Colour slot"
          hint="A palette slot name (ADR 0051), never a hex: PrintInk, Butter, FxRed."
          mono
          value={t.colorSlot}
          issues={errors.colorSlot}
          onChange={(v) => set('colorSlot', v)}
        />
        <p className="text-xs text-bruise sm:col-span-2">
          Order on this page is the tier index. At least two rows; the first has capacity 1;
          capacity and recovery never go up from one row to the next.
        </p>
      </Fieldset>
      <Fieldset legend="Tank">
        {num('capacityMult', 'Capacity multiplier', 0.05, 1, 'The tank at this tier.')}
        {num('recoveryMult', 'Recovery multiplier', 0.05, 1, 'Recovery at this tier.')}
        {num(
          'biteOddsMult',
          'Bite odds multiplier',
          1,
          20,
          'A push-off bite roll at this tier.',
          0.5,
        )}
        {num(
          'climbBackCalmS',
          'Calm to climb back (s)',
          0,
          300,
          'Calm seconds at a full bar before climbing back from this tier. Unused on the first row.',
          1,
        )}
      </Fieldset>
      <Fieldset legend="Penalties">
        {num('swingIntervalMult', 'Swing interval multiplier', 0.5, 2, 'Slower swings above 1.')}
        {num('moveSpeedMult', 'Move speed multiplier', 0.5, 2, 'Slower walking below 1.')}
        {num('acquireMult', 'Acquire multiplier', 0.5, 2, 'Slower target pick-up above 1.')}
        {num('spreadMult', 'Spread multiplier', 0.5, 2, 'Wider gun spread above 1.')}
      </Fieldset>
    </>
  );
}
